import React, { useState, useEffect } from 'react';
import { Star, Send, User, CheckCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const Reviews = ({ cartCount }) => {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average_rating: 0, rating_distribution: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    rating: 5,
    title: '',
    review_text: ''
  });

  // Load reviews
  useEffect(() => {
    const loadReviews = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/reviews`);
        const data = await response.json();
        setReviews(data.reviews || []);
        setStats({
          total: data.total || 0,
          average_rating: data.average_rating || 0,
          rating_distribution: data.rating_distribution || {}
        });
      } catch (error) {
        console.error('Error loading reviews:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadReviews();
  }, []);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast({
          title: 'Thank you! ⭐',
          description: 'Your review has been submitted successfully.',
        });
        setShowForm(false);
        setFormData({ customer_name: '', customer_email: '', rating: 5, title: '', review_text: '' });
        // Reload reviews
        const data = await fetch(`${API_BASE_URL}/api/reviews`).then(r => r.json());
        setReviews(data.reviews || []);
        setStats({ total: data.total, average_rating: data.average_rating, rating_distribution: data.rating_distribution });
      } else {
        throw new Error('Failed to submit review');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit review. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Star rating component
  const StarRating = ({ rating, onRate, interactive = false, size = 'md' }) => {
    const sizeClasses = {
      sm: 'w-4 h-4',
      md: 'w-6 h-6',
      lg: 'w-8 h-8'
    };
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate && onRate(star)}
            disabled={!interactive}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <Star
              className={`${sizeClasses[size]} ${
                star <= rating 
                  ? 'fill-amber-400 text-amber-400' 
                  : 'fill-gray-300 text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Generate structured data for reviews
  const reviewsSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Adina's Crafts",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": stats.average_rating || 5,
      "reviewCount": stats.total || 1,
      "bestRating": 5,
      "worstRating": 1
    },
    "review": reviews.slice(0, 10).map(review => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.customer_name
      },
      "datePublished": review.created_at,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": 5,
        "worstRating": 1
      },
      "reviewBody": review.review_text
    }))
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Customer Reviews | Adina's Crafts - Handmade Candles UK</title>
        <meta name="description" content="Read customer reviews of Adina's Crafts handmade candles. See what our customers say about our scented soya wax candles, elephant candles, and resin designs. 5-star rated UK candle shop." />
        <meta name="keywords" content="Adina's Crafts reviews, handmade candles reviews UK, customer reviews candles, best candle shop UK reviews, scented candles testimonials" />
        <link rel="canonical" href="https://www.adinascrafts.co.uk/reviews" />
        
        <script type="application/ld+json">
          {JSON.stringify(reviewsSchema)}
        </script>
      </Helmet>

      <Navbar cartCount={cartCount} />

      {/* Hero Section */}
      <section className="py-16 text-center bg-gradient-to-b from-black to-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-amber-100 mb-4">
            Customer Reviews
          </h1>
          <p className="text-lg text-amber-200/70 max-w-2xl mx-auto mb-6">
            See what our lovely customers say about our handcrafted creations
          </p>
          
          {/* Overall Rating */}
          {stats.total > 0 && (
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-3">
                <span className="text-5xl font-bold text-amber-400">{stats.average_rating}</span>
                <div>
                  <StarRating rating={Math.round(stats.average_rating)} size="lg" />
                  <p className="text-amber-200/60 text-sm mt-1">Based on {stats.total} reviews</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <main className="container mx-auto px-6 py-12">
        {/* Write Review Button */}
        <div className="text-center mb-12">
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-amber-400 hover:bg-amber-500 text-black font-semibold px-8 py-3 text-lg"
          >
            <Star className="w-5 h-5 mr-2" />
            {showForm ? 'Cancel' : 'Write a Review'}
          </Button>
        </div>

        {/* Review Form */}
        {showForm && (
          <div className="max-w-2xl mx-auto mb-12 bg-gray-900/50 rounded-xl p-8 border border-gray-800">
            <h2 className="text-2xl font-serif font-bold text-amber-100 mb-6 text-center">
              Share Your Experience
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rating */}
              <div className="text-center">
                <label className="block text-amber-200 mb-3">Your Rating</label>
                <div className="flex justify-center">
                  <StarRating 
                    rating={formData.rating} 
                    onRate={(r) => setFormData({...formData, rating: r})}
                    interactive={true}
                    size="lg"
                  />
                </div>
              </div>
              
              {/* Name & Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-amber-200 mb-2">Your Name *</label>
                  <Input
                    type="text"
                    required
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    placeholder="John Smith"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-amber-200 mb-2">Your Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.customer_email}
                    onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                    placeholder="john@example.com"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>
              
              {/* Review Title */}
              <div>
                <label className="block text-amber-200 mb-2">Review Title *</label>
                <Input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Amazing candles!"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              {/* Review Text */}
              <div>
                <label className="block text-amber-200 mb-2">Your Review *</label>
                <Textarea
                  required
                  value={formData.review_text}
                  onChange={(e) => setFormData({...formData, review_text: e.target.value})}
                  placeholder="Tell us about your experience with our products..."
                  rows={5}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-400 hover:bg-amber-500 text-black font-semibold py-3"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-5 h-5" />
                    Submit Review
                  </span>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Rating Distribution */}
        {stats.total > 0 && (
          <div className="max-w-md mx-auto mb-12 bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h3 className="text-lg font-semibold text-amber-100 mb-4">Rating Breakdown</h3>
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = stats.rating_distribution[rating] || 0;
              const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
              return (
                <div key={rating} className="flex items-center gap-3 mb-2">
                  <span className="text-amber-200 w-8">{rating}★</span>
                  <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                    <div 
                      className="bg-amber-400 h-full rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-amber-200/60 w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Reviews List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-amber-200">Loading reviews...</p>
          </div>
        ) : reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review) => (
              <article 
                key={review.id}
                className="bg-gray-900/50 rounded-xl p-6 border border-gray-800 hover:border-amber-400/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-400/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-100">{review.customer_name}</p>
                      <p className="text-xs text-amber-200/50">{formatDate(review.created_at)}</p>
                    </div>
                  </div>
                  {review.is_verified && (
                    <span className="flex items-center gap-1 text-xs text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Verified
                    </span>
                  )}
                </div>
                
                <StarRating rating={review.rating} size="sm" />
                
                <h3 className="font-semibold text-amber-100 mt-3 mb-2">{review.title}</h3>
                <p className="text-amber-200/70 text-sm leading-relaxed">{review.review_text}</p>
                
                {review.product_name && (
                  <p className="mt-3 text-xs text-amber-400">
                    Purchased: {review.product_name}
                  </p>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-900/50 rounded-xl border border-gray-800">
            <Star className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-serif text-amber-100 mb-2">No Reviews Yet</h3>
            <p className="text-amber-200/60 mb-6">Be the first to share your experience!</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-amber-400 hover:bg-amber-500 text-black font-semibold"
            >
              Write the First Review
            </Button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Reviews;
